import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api, type AcctCorp, type OpsTransitConfig, type OpsTransitRow, type OpsTransitRun, type OpsXrefRow } from '../lib/api';
import { ErrorBox, dash, money } from '../components/DataTable';
import { canScreen, useMe } from '../lib/me';
import { link, urlParam } from '../lib/deepLink';
import { useAcctCorp } from '../lib/sticky';
import { FitBox } from '../components/FitBox';

/**
 * DYOPS 대중교통비·선지급 → 버스인웹 자동 반영 현황(2026-09-30 way · 개선요청 #90). 서버 `functions/src/opsTransitSync.ts`.
 *
 * 신청이 DYOPS 에 들어오면 출납일보에 **지급요청일 예정 줄**, 회계가 DYOPS 에서 지급 체크하면 **지급일로 옮기고 영업정산**
 * (기사부담 공제 · 거래처부담 청구 추가 · 회사부담 수익 비용 · 선지급 공제)을 넣는다. 이 탭은 그 결과와 못 넣은 사유를 본다.
 * 회계가 이미 손으로 넣은 출납 줄(적요의 DY 번호)은 **연결만** 하고 건드리지 않는다.
 *
 * 2026-10-02 way(월보구 증빙 · 이중 지급 찾기): 출납·영업정산 칸이 **그 줄로 가는 링크** · 같은 DY 번호의 줄을 전부 모아
 * (`opsTransitXref`) 출납 2줄↑·영업정산 2줄↑·출납 재무추가 공제와 영업정산 공제 겹침을 「중복」으로 · 영업정산이 있어도
 * 그 접수가 그 달 청구 전이면(미수 게이트) 「청구 대기 — 명세 미반영」. `?dy=DY…` 로 들어오면 그 줄만.
 */

const CASH_LABEL: Record<string, string> = { ops: '자동 줄', manual: '손입력 줄 연결', removed: '사람이 지움' };
const REQ_LABEL: Record<string, string> = { ops: '반영됨', removed: '사람이 지움' };
const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const chip = 'rounded border border-line px-1 py-0.5 text-[11px] text-brand-700 hover:bg-brand-50';

type Filter = 'all' | 'issue' | 'waiting';
type Kind = 'all' | 'transit' | 'advance';

/**
 * 구분 토글 기억(#121) — 이 브라우저에만(localStorage · 막혀도 화면은 돈다). 법인은 회계 화면 공용 `useAcctCorp`(계정 저장)를 따른다.
 */
const KIND_KEY = 'businweb.opsTransit.kind';
function loadKind(): Kind {
  try {
    const v = localStorage.getItem(KIND_KEY);
    return v === 'transit' || v === 'advance' ? v : 'all';
  } catch { return 'all'; }
}
function saveKind(v: Kind): void {
  try { localStorage.setItem(KIND_KEY, v); } catch { /* 기억 못 해도 화면은 돈다 */ }
}

/**
 * 접수시각(KST) — DY 번호는 신청 시각의 시트 serial 이다(`opsTransitCore.serialNo` 의 역 · 0.864초 단위 반올림).
 * 'DY4629756553' → 46297.56553 → 2026-10-02 13:34. 모양이 다르면 null.
 */
export function dyTime(dy: string | null | undefined): string | null {
  const m = /^DY(\d{5})(\d*)$/.exec(String(dy ?? ''));
  if (!m) return null;
  const serial = Number(`${m[1]}.${m[2] || '0'}`);
  const d = new Date(Math.round((serial - 25_569) * 86_400_000));
  return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 16).replace('T', ' ');
}

/** 이번 달(KST) 'YYYY-MM' — 정산월이 이보다 앞이면 「달 지남」. */
const thisMonKst = () => new Date(Date.now() + 9 * 3_600_000).toISOString().slice(0, 7);

/**
 * 같은 DY 번호로 모은 줄에서 읽는 경고(중복·청구 대기). 서버는 목록만 주고 판정은 여기 한 곳.
 * 10-16 way: 선지급은 청구 요청 없이 먼저 지급한다 → 청구 요청이 안 올라온 선지급은 「선지급 청구 전」,
 * 정산월이 지났으면 `overdue`(빨강) — 돈은 나갔는데 청구가 없으면 운행료·공제 모두 명세에 못 들어간다.
 */
export function flagsOf(x: OpsXrefRow | undefined): { dup: string[]; held: string | null; overdue: boolean } {
  if (!x) return { dup: [], held: null, overdue: false };
  const dup: string[] = [];
  if (x.cash_lines.length > 1) {
    const manual = x.cash_lines.filter((c) => c.source !== 'ops').length;
    dup.push(`출납 ${x.cash_lines.length}줄${manual && manual < x.cash_lines.length ? '(자동+손입력)' : ''} — 이중 출금 의심`);
  }
  if (x.req_lines.length > 1) dup.push(`영업정산 ${x.req_lines.length}줄 — 이중 공제 의심`);
  const deducted = x.cash_lines.filter((c) => c.deducted);
  if (deducted.length && x.req_lines.some((r) => r.driverMinus)) {
    dup.push(`출납 공제(재무추가 NO ${deducted.map((c) => c.deducted).join(',')})와 영업정산 공제가 겹침`);
  }
  const h = x.req_lines.find((r) => r.held);
  const advance = x.kind === 'advance';
  const held = h
    ? `${advance ? '선지급 청구 전' : '청구 대기'} — 접수 ${h.recv ?? '?'} ${h.mon ?? ''} 청구 요청 없음(미수 ${h.misuState ?? '등록 없음'}) · 명세 미반영`
    : null;
  const overdue = !!h && advance && !!h.mon && h.mon < thisMonKst();
  return { dup, held, overdue };
}

export function OpsTransitTab({ corps }: { corps: AcctCorp[] }) {
  const { me } = useMe();
  const canWrite = canScreen(me, 'accounting', 'write');
  const { search } = useLocation();
  const wantDy = urlParam(search, 'dy');
  const [rows, setRows] = useState<OpsTransitRow[]>([]);
  const [xref, setXref] = useState<Map<string, OpsXrefRow>>(new Map());
  const [cfg, setCfg] = useState<OpsTransitConfig | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState<Filter>(wantDy ? 'all' : 'issue');
  const [term, setTerm] = useState(wantDy ?? '');
  const [corpId, setCorpId] = useAcctCorp();
  const [kind, setKindState] = useState<Kind>(loadKind);
  const setKind = (v: Kind) => { setKindState(v); saveKind(v); };
  // DY 링크로 들어오면 그 줄이 법인·구분에 가려지지 않게 둘 다 풀고 읽는다(고른 값은 그대로 기억).
  const qCorp = wantDy ? null : corpId;
  const qKind = wantDy || kind === 'all' ? null : kind;
  const [run, setRun] = useState<OpsTransitRun | null>(null);
  const [loadedAt, setLoadedAt] = useState('');
  const [reloading, setReloading] = useState(false);
  const focusRef = useRef<HTMLTableRowElement | null>(null);

  // 링크로 다시 들어오면(같은 탭이 떠 있어도) 그 DY 로.
  useEffect(() => { if (wantDy) { setTerm(wantDy); setFilter('all'); } }, [wantDy]);

  const load = useCallback(async () => {
    setErr('');
    try {
      const [r, x] = await Promise.all([api.listOpsTransit({ corpId: qCorp, kind: qKind }), api.opsTransitXref({ all: true })]);
      setRows(r.rows);
      setCfg(r.config);
      setXref(new Map(x.rows.map((v) => [v.dy_no, v])));
      setLoadedAt(new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }));
    } catch (e) { setErr(errMsg(e)); }
  }, [qCorp, qKind]);
  // #436 way — 수시로 확인하는 화면이라 다시 불러오기 버튼(화면 탭이 살아 있어 메뉴를 다시 눌러도 안 새로 읽는다).
  const reload = async () => { setReloading(true); try { await load(); } finally { setReloading(false); } };
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { focusRef.current?.scrollIntoView({ block: 'center' }); }, [rows, wantDy]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true); setErr('');
    try { await fn(); await load(); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };

  const isIssue = useCallback((r: OpsTransitRow) => {
    const f = flagsOf(r.dy_no ? xref.get(r.dy_no) : undefined);
    return !!(r.cash_error || r.warn || (r.req_error && !/버스인 등록을 마치면/.test(r.req_error)) || r.cash_mode === 'removed'
      || r.req_mode === 'removed' || f.dup.length || f.held);
  }, [xref]);

  const shown = useMemo(() => {
    const t = term.trim().toUpperCase();
    return rows.filter((r) => {
      if (t && ![r.dy_no, r.payee, r.client, r.corp_label].some((v) => String(v ?? '').toUpperCase().includes(t))) return false;
      if (filter === 'all') return true;
      if (filter === 'issue') return isIssue(r);
      return !!r.req_error && /버스인 등록을 마치면/.test(r.req_error);
    });
  }, [rows, filter, term, isIssue]);

  const counts = useMemo(() => ({
    total: rows.length,
    cashOps: rows.filter((r) => r.cash_mode === 'ops').length,
    cashManual: rows.filter((r) => r.cash_mode === 'manual').length,
    req: rows.filter((r) => r.req_mode === 'ops').length,
    issue: rows.filter(isIssue).length,
    dup: rows.filter((r) => flagsOf(r.dy_no ? xref.get(r.dy_no) : undefined).dup.length).length,
    held: rows.filter((r) => flagsOf(r.dy_no ? xref.get(r.dy_no) : undefined).held).length,
    overdue: rows.filter((r) => flagsOf(r.dy_no ? xref.get(r.dy_no) : undefined).overdue).length,
  }), [rows, xref, isIssue]);

  return (
    <div className="space-y-2">
      <div className="card flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
        <b>OPS 대중교통비·선지급 자동 반영</b>
        {/* 09-30 way 「버튼 켜고 끄고 혼선」 — 켜기/끄기·미리보기·지금 반영 버튼은 없앴다. 스위치는 서버(ops_transit_config)에 비상 정지용으로만 남는다. */}
        <span className={`rounded px-2 py-0.5 text-xs font-bold ${cfg?.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {cfg ? (cfg.enabled ? '바로 반영 중(10분마다 다시 확인)' : '자동 반영 멈춤 — 관리자에게 알려 주세요') : '…'}
        </span>
        <span className="text-xs text-ink-soft">정산년월 {cfg?.from_ym ?? '—'} 부터</span>
        <button className="btn-ghost px-2.5 py-1 text-xs" onClick={() => void reload()} disabled={reloading}
                title="DYOPS 에서 방금 바뀐 내용까지 다시 읽습니다">
          {reloading ? '불러오는 중…' : '↻ 새로고침'}
        </button>
        {loadedAt && <span className="text-xs text-ink-faint">{loadedAt} 기준</span>}
        <span className="ml-auto text-xs text-ink-soft">
          연결 {counts.total} · 자동 출납 {counts.cashOps} · 손입력 연결 {counts.cashManual} · 영업정산 {counts.req} · 확인 필요 {counts.issue}
          {counts.dup > 0 && <b className="text-red-700"> · 중복 의심 {counts.dup}</b>}
          {counts.held > 0 && <b className="text-amber-800"> · 청구 대기 {counts.held}</b>}
          {counts.overdue > 0 && <b className="text-red-700"> · 선지급 청구 없이 정산월 지남 {counts.overdue}</b>}
        </span>
      </div>
      <p className="page-hint px-1">
        DYOPS 에서 신청·버스인 등록·지급·수정·삭제하면 <b>바로</b> 반영합니다(10분마다 한 번 더 확인).
        신청 = 출납일보에 <b>예정 줄</b>(지급요청일과 접수 기준일 중 늦은 날 — 오후 2시 전 접수는 그날, 2시부터는 다음 영업일) · DYOPS 에서 회계 지급 체크 = <b>지급일로 옮기고 영업정산</b>(기사부담 공제 · 거래처부담 청구 추가 · 회사부담 수익 비용 · 선지급 공제 총액).
        적요에 같은 DY 번호가 있는 손입력 줄은 연결만 합니다. 사람이 지운 줄은 다시 만들지 않습니다(「다시 시도」로 풉니다).
        출납·영업정산 칸의 링크를 누르면 그 줄로 갑니다. 같은 DY 번호로 출납·영업정산이 두 줄 이상이면 <b className="text-red-700">중복</b>으로 표시합니다.
      </p>
      {err && <ErrorBox message={err} />}
      {run && (
        <div className="card p-3 text-xs">
          <div className="mb-1 font-semibold">{run.dryRun ? '미리보기(쓰기 없음)' : '반영 결과'} · 정산년월 {run.fromYm}~ · 문서 {run.docs}건</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {Object.entries(run.counts).sort().map(([k, v]) => <span key={k}>{k} <b>{v}</b></span>)}
          </div>
          {run.items.filter((i) => i.cashError || i.reqError).slice(0, 20).map((i) => (
            <div key={i.docId} className="mt-1 text-ink-soft">{i.dyNo ?? i.docId}: {i.cashError ?? ''} {i.reqError ?? ''}</div>
          ))}
          <button className="mt-1 text-ink-faint underline" onClick={() => setRun(null)}>닫기</button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-1 text-sm">
        {([['issue', '확인 필요'], ['waiting', '버스인 번호 대기'], ['all', '전체']] as [Filter, string][]).map(([id, label]) => (
          <button key={id} onClick={() => setFilter(id)}
                  className={`rounded-lg px-2.5 py-1 ${filter === id ? 'bg-brand-600 font-semibold text-white shadow-sm' : 'text-ink hover:bg-brand-50'}`}>{label}</button>
        ))}
        <span className="mx-1 h-5 w-px bg-line" aria-hidden />
        {([['all', '전체'], ['transit', '대중교통비'], ['advance', '선지급']] as [Kind, string][]).map(([id, label]) => (
          <button key={id} onClick={() => setKind(id)} title="구분"
                  className={`rounded-lg px-2.5 py-1 ${kind === id ? 'bg-brand-600 font-semibold text-white shadow-sm' : 'text-ink hover:bg-brand-50'}`}>{label}</button>
        ))}
        <select className="field ml-1 w-32" value={corpId ?? ''} aria-label="법인" title="법인(고른 법인만)"
                onChange={(e) => setCorpId(e.target.value ? Number(e.target.value) : null)}>
          <option value="">전체 법인</option>
          {corps.map((c) => <option key={c.corp_id} value={c.corp_id}>{c.parent_corp_id ? `└ ${c.short_name}` : c.short_name}</option>)}
        </select>
        {wantDy && (corpId !== null || kind !== 'all') && <span className="text-xs text-ink-faint">DY 링크라 법인·구분을 풀고 보여 줍니다</span>}
        <input className="field ml-auto w-56" placeholder="DY 번호·이름·거래처" value={term} onChange={(e) => setTerm(e.target.value)} />
      </div>
      <FitBox deps={[rows, shown.length, filter, kind, corpId, run, err]}>
        <table className="w-full text-sm">
          <thead className="sticky top-0">
            <tr className="text-left text-xs text-ink-soft">
              {['DY 번호', '정산년월', '구분', '법인', '거래처', '고객/예금주', '금액', '접수시각', '지급일', '출납일보', '영업정산', '사유·경고', ''].map((h) => (
                <th key={h} className="whitespace-nowrap bg-white px-2 py-1.5">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td colSpan={13} className="px-2 py-6 text-center text-ink-faint">{rows.length ? '해당하는 줄이 없습니다.' : '아직 반영한 문서가 없습니다.'}</td></tr>
            )}
            {shown.map((r) => {
              const x = r.dy_no ? xref.get(r.dy_no) : undefined;
              const f = flagsOf(x);
              const focus = !!wantDy && r.dy_no === wantDy;
              const extraCash = (x?.cash_lines ?? []).filter((c) => c.id !== r.cash_book_id);
              const extraReq = (x?.req_lines ?? []).filter((q) => q.id !== r.result_req_id);
              const reqWhy = r.req_error ?? (r.req_mode ? null : !r.paid_date ? '지급 체크 전 — 영업정산은 회계 지급 뒤에 들어갑니다' : null);
              return (
                <tr key={r.doc_id} ref={focus ? focusRef : undefined}
                    className={`border-t border-line ${r.doc_gone ? 'text-ink-faint line-through' : ''} ${focus ? 'bg-amber-50 outline outline-2 outline-amber-400' : ''}`}>
                  <td className="whitespace-nowrap px-2 py-1 font-mono text-xs">{dash(r.dy_no)}</td>
                  <td className="whitespace-nowrap px-2 py-1">{dash(r.settle_ym)}</td>
                  <td className="whitespace-nowrap px-2 py-1">{r.kind === 'advance' ? `선지급 ${dash(r.burden)}` : `대중교통비 ${dash(r.burden)}`}</td>
                  <td className="whitespace-nowrap px-2 py-1">{dash(r.corp_label)}</td>
                  <td className="max-w-[14rem] truncate px-2 py-1" title={r.client ?? ''}>{dash(r.client)}</td>
                  <td className="whitespace-nowrap px-2 py-1">{dash(r.payee)}</td>
                  <td className="whitespace-nowrap px-2 py-1 text-right">{money(r.amount)}</td>
                  <td className="whitespace-nowrap px-2 py-1 text-xs" title="DYOPS 신청 시각(KST) — 오후 2시부터는 다음 영업일 출납일보">{dash(dyTime(r.dy_no))}</td>
                  <td className="whitespace-nowrap px-2 py-1">{r.paid_date ?? <span className="text-ink-faint">지급 전</span>}</td>
                  <td className="whitespace-nowrap px-2 py-1 text-xs">
                    {r.cash_mode ? CASH_LABEL[r.cash_mode] : '—'}
                    {r.cash_book_id && r.cash_mode !== 'removed' && (
                      <Link className={`${chip} ml-1`} to={link.cashBook(r.cash_book_id, r.cash_pay_date)} title="출납일보에서 이 줄 보기">
                        {r.cash_pay_date ?? '출납'} · {money(r.cash_amount)}
                      </Link>
                    )}
                    {extraCash.map((c) => (
                      <Link key={c.id} className={`${chip} ml-1 border-red-300 text-red-700`} to={link.cashBook(c.id, c.date)}
                            title={`같은 DY 번호의 ${c.source === 'ops' ? '자동' : '손입력'} 줄 · ${c.corp ?? ''} ${c.summary ?? ''}`}>
                        +{c.source === 'ops' ? '자동' : '손입력'} {c.date} · {money(c.amount)}
                      </Link>
                    ))}
                    {!r.cash_book_id && r.cash_error && <div className="text-red-700">출납 미반영 — {r.cash_error}</div>}
                  </td>
                  <td className="whitespace-nowrap px-2 py-1 text-xs">
                    {r.req_mode ? REQ_LABEL[r.req_mode] : '—'}
                    {r.result_req_id && r.req_mode === 'ops' && (
                      <Link className={`${chip} ml-1`} to={link.resultReq(r.result_req_id, r.req_mon)} title="영업정산에서 이 줄 보기">
                        {r.req_mon} {r.req_account} {money(r.req_amount)}
                      </Link>
                    )}
                    {extraReq.map((q) => (
                      <Link key={q.id} className={`${chip} ml-1 border-red-300 text-red-700`} to={link.resultReq(q.id, q.mon)}
                            title={`같은 DY 번호의 영업정산 줄 · ${q.by ?? ''}`}>
                        +{q.mon} {q.account} {money(q.amount)}
                      </Link>
                    ))}
                    {reqWhy && <div className={r.req_error ? 'text-red-700' : 'text-ink-soft'}>영업정산 미반영 — {reqWhy}</div>}
                    {f.held && (
                      <div className={`font-medium ${f.overdue ? 'text-red-700' : 'text-amber-800'}`}>
                        {f.overdue && '⚠ 정산월 지남 · '}{f.held}
                        {/* 10-16 회의 #36 — 청구임시 폐지. 게이트는 영업의 「청구 요청」(청구대상)부터 열린다. */}
                        <span className="ml-1 font-normal text-ink-soft">— 영업이 거래처별 정산에서 「청구 요청」을 올리면 열립니다</span>
                      </div>
                    )}
                  </td>
                  <td className="max-w-[24rem] px-2 py-1 text-xs text-red-700">
                    {f.dup.map((d) => <div key={d} className="font-bold">⚠ 중복: {d}</div>)}
                    {[r.cash_error, r.req_error, r.warn].filter(Boolean).join(' / ')}
                  </td>
                  <td className="whitespace-nowrap px-2 py-1">
                    {canWrite && !r.doc_gone && (r.cash_error || r.req_error || r.cash_mode === 'removed' || r.req_mode === 'removed') && (
                      <button className="btn-ghost px-2 py-0.5 text-xs" disabled={busy}
                              onClick={() => act(async () => setRun(await api.retryOpsTransit({ docId: r.doc_id })))}>다시 시도</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </FitBox>
    </div>
  );
}
